import { useEffect } from "react";

import { Icon, LanguageSVG } from "@newsela/angelou";
import { actionNames, objectTypes } from "@newsela/snitch";

import ContentLevel from "@/types/models/ContentLevel.interface";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import SecondaryActionButton from "@/view/SecondaryActionsToolbar/SecondaryActionButton";
import useTranslatorControlsContext from "@/view/utils/useTranslatorControlsContext";
import { iconCss } from "./TranslateAction.style";

interface TranslateActionProps {
  activeSubMenu: string | null | undefined;
  currentLevel: ContentLevel | undefined;
  disabled: boolean;
  hoverLabel: string;
  isMobile: boolean | undefined;
  isTranslateActionUnavailable: boolean | undefined;
}

const TranslateAction = ({
  activeSubMenu,
  currentLevel,
  disabled,
  hoverLabel,
  isMobile = false,
  isTranslateActionUnavailable = false,
}: TranslateActionProps) => {
  const { isTranslateMode, setIsTranslateMode } =
    useTranslatorControlsContext();
  const { isReadAloudModeActive } = useReadAloudMode();

  useEffect(() => {
    if (isReadAloudModeActive) setIsTranslateMode(false);
  }, [isReadAloudModeActive]);

  if (isMobile && !!activeSubMenu) return null;

  const eventProperties = {
    objectType: objectTypes.content,
    actionName: actionNames.translate,
    streamProperties: {
      content_type: "Article",
      description: isTranslateActionUnavailable
        ? "display translations unavailable tooltip"
        : isTranslateMode
          ? "stop translations"
          : "start translations",
      content_id: currentLevel?.id,
      article_level: currentLevel?.lexile,
    },
  };

  const onClick = () => {
    if (!isTranslateActionUnavailable) {
      setIsTranslateMode(!isTranslateMode && !isReadAloudModeActive);
    }
  };

  const actionName = "Translate";
  const ariaProps =
    isTranslateActionUnavailable && !disabled
      ? {
          "aria-label": "Translations are not available on this article",
          "aria-disabled": false,
        }
      : {
          "aria-label": `turn ${isTranslateMode ? "off" : "on"} translate mode`,
          "aria-pressed": isTranslateMode,
          "aria-disabled": isReadAloudModeActive,
        };
  const qaSelector =
    isTranslateActionUnavailable && !disabled
      ? "translations-unavailable-button"
      : isTranslateMode
        ? "turn-off-translations"
        : "turn-on-translations";
  const icon = <Icon __cssFor={{ root: iconCss }} SvgComponent={LanguageSVG} />;

  return (
    <SecondaryActionButton
      ariaProps={ariaProps}
      dataProps={{ "data-qa-selector": qaSelector }}
      disabled={disabled}
      eventProperties={eventProperties}
      hoverLabel={hoverLabel}
      icon={icon}
      isActive={isTranslateMode}
      isButtonActionUnavailable={isTranslateActionUnavailable && !disabled}
      onClick={onClick}
      title={actionName}
    />
  );
};

export default TranslateAction;
